import {observer} from "mobx-react";
import classNames from "classnames";
import {useModalStore} from "@/Stores/Site/ModalStore";
import {useBasketStore} from "@/Stores/Site/Basket";
import {useSearchStore} from "@/Stores/Site/Search";
import {selectSetSearchQuery} from "@/Stores/Site/Search/Selectors";
import {User} from "@/entities/user";
import {BasketButton} from "@/entities/basket";
import {useSearch} from "@/features/search";
import {useScrollDirection} from "@/features/header";
import {useIsActiveRoute} from "@/shared/lib/hooks/useIsActiveRoute";
import {Link} from "@/shared/ui/Link";
import {Picture} from "@/shared/ui/Picture";
import {Svg} from "@/shared/ui/Svg";
import {useStoreMobx} from "@/shared/lib/mobx/useStoreMobx";
import {useMobileMenuDrawerStore} from "@/widgets/mobile-menu-drawer/model/store/mobileMenuDrawerStore";
import {useCatalogMenu} from "@/widgets/catalog-menu";
import {ProfileIcon} from "@/widgets/header/ui/Icons/ProfileIcon";
import styles from "./HeaderMain.module.scss";

type Props = {
    authCheck: boolean;
    user: User;
    imgVersion: string;
    catalogMenu: string[];
}

export const HeaderMain = observer((
    {
        authCheck = false,
        user,
    }: Props
) => {
    const {
        opened,
        inputRef,
        query,
        handleChange,
        handleKeyDown,
        handleFocus,
        handleBlur,
        submitSearch,
        history,
        cursorHistory,
        clearHistory,
        suggestions,
        cursorSuggestions
    } = useSearch();

    const activeOrders = !authCheck ? 0 : user.ordersCount + user.refundsCount;

    const openSearchModal = useModalStore(s => s.openSearchModal);
    // const closeSearchModal = useModalStore(s => s.closeSearchModal);
    const basketCount = useBasketStore(s => s.basketCount);
    const openMobileMenu = useMobileMenuDrawerStore(s => s.actions.openModal);
    const searchStoreSetSearchQuery = useSearchStore(selectSetSearchQuery);

    const catalogMenuDrawer = useCatalogMenu();

    const {favouritesStore} = useStoreMobx();

    const isActiveRoute = useIsActiveRoute();

    const {scrollY} = useScrollDirection();
    const containerClasses = classNames(
        styles.mainContainer,
        {
            [styles.compact]: scrollY > 120,
        }
    );

    const catalogIcon = catalogMenuDrawer.opened ? `dagger-close` : `catalog`;

    /**
     * Обработка истории запросов
     * @param item
     */
    const onClickHistory = (item) => {
        // // Закрываем модалку
        // closeSearchModal();
        // Добавляем фразу (на которую нажали) в поисковой запрос
        searchStoreSetSearchQuery(item);
        // Запускаем поиск
        submitSearch();
    };

    /**
     * Удалить поисковой запрос
     */
    const deleteSearchQuery = () => {
        searchStoreSetSearchQuery();
    };

    return (
        <div className={containerClasses}>
            <div className={`globalContainer`}>
                <div className={styles.container}>
                    <div className={styles.buttonsContainer}>
                        <div className={styles.mobileMenuButtonContainer} onClick={openMobileMenu}>
                            <Svg id={`headerMenuIcon`} className={styles.mobileMenuIcon} />
                        </div>
                        <div className={styles.catalogButtonContainer} onClick={catalogMenuDrawer.toggle}>
                            <Svg id={catalogIcon} className={styles.catalogButtonIcon} />
                            <p className={styles.catalogButtonText}>Каталог</p>
                        </div>
                        <div className={classNames(styles.searchContainer, {[styles.opened]: opened})}>
                            <input
                                ref={inputRef}
                                name={`search`}
                                type={`text`}
                                placeholder={`Найти в каталоге`}
                                value={query}
                                autoComplete={`off`}
                                onChange={handleChange}
                                onKeyUp={handleKeyDown}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                className={styles.searchInput}
                            />
                            <div className={styles.searchButtons}>
                                {query !== '' && (
                                    <button
                                        type={`button`}
                                        className={styles.searchCrossContainer}
                                        onClick={deleteSearchQuery}
                                        onMouseDown={(e) => e.preventDefault()}
                                    >
                                        <Svg id={`cross`} className={styles.searchCrossIcon} />
                                    </button>
                                )}
                                <button
                                    type={`button`}
                                    className={classNames(styles.searchSubmitButton, {[styles.active]: query !== ''})}
                                    onClick={submitSearch}
                                >
                                    <Svg
                                        id={`search`}
                                        className={classNames(styles.searchSubmitIcon, {[styles.active]: query !== ''})}
                                    />
                                </button>
                            </div>
                            <div className={styles.searchContent}>
                                {history.length > 0 && suggestions.length === 0 && (
                                    <div className={styles.searchHistory}>
                                        <div className={styles.searchHistoryHead}>
                                            <p className={styles.searchHistoryTitle}>Ранее вы искали</p>
                                            <p className={styles.searchHistoryClear} onClick={clearHistory}>Очистить</p>
                                        </div>
                                        <div className={styles.searchHistoryBody}>
                                            {history.map((item, index) => {
                                                return (
                                                    <div
                                                        key={index}
                                                        onClick={() => onClickHistory(item)}
                                                        className={classNames(styles.searchHistoryItem, {[styles.focused]: cursorHistory === index})}
                                                    >
                                                        <Svg
                                                            id={`searchHistory`}
                                                            className={styles.searchHistorySvg}
                                                        />
                                                        <p className={styles.searchHistoryText}>{item}</p>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                                {suggestions.length > 0 && (
                                    <div className={styles.searchSuggestions}>
                                        {suggestions.map((el, index) => {
                                            return (
                                                <Link
                                                    key={index}
                                                    href={`${el.url}`}
                                                    className={classNames(styles.searchSuggestionsItem, {[styles.focused]: cursorSuggestions === index})}
                                                >
                                                    <div className={styles.searchSuggestionsIconContainer}>
                                                        {el.img === 'catalog'
                                                            ? <Svg
                                                                id={`catalog`}
                                                                className={styles.searchSuggestionsSvg}
                                                            />
                                                            : <Picture
                                                                src={el.img}
                                                                alt={el.h1}
                                                                classes={{root: styles.searchSuggestionsIcon}}
                                                            />
                                                        }
                                                    </div>
                                                    <p className={styles.searchSuggestionsText}>{el.h1}</p>
                                                </Link>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/*<Link*/}
                    {/*    href={'/'}*/}
                    {/*    className={classNames(styles.logoContainer, {[styles.hidden]: !isActiveRoute('/')})}*/}
                    {/*>*/}
                    {/*    <Svg id={`main-logo`} className={styles.logoIcon} />*/}
                    {/*</Link>*/}
                    <Link href={'/'} className={classNames(styles.logoContainer)}>
                        <Svg id={`main-logo`} className={styles.logoIcon} />
                    </Link>
                    <div className={styles.iconsContainer}>
                        <div className={styles.searchIconContainer} onClick={() => openSearchModal()}>
                            <Svg id={`search`} className={styles.searchIcon} />
                        </div>
                        <ProfileIcon authCheck={authCheck} activeOrders={activeOrders} />
                        <Link className={styles.favoritesIconContainer} href={`/favorites`}>
                            <Svg id={`heart-header`} className={styles.favoritesIcon} />
                            {favouritesStore.favouritesData.length > 0 && (
                                <span className={styles.favoritesIconDot}></span>
                            )}
                        </Link>
                        <BasketButton className={styles.basketIconContainer}>
                            <Svg id={`headerBasket`} className={styles.basketIcon} />
                            {basketCount > 0 && (<span className={styles.basketCount}>{basketCount}</span>)}
                        </BasketButton>
                    </div>
                </div>
            </div>
        </div>
    );
});
